import {
  type DeleteInboxDraftMutation,
  type FetchResult,
  type MutationResult,
  useDeleteInboxDraftMutation,
} from "@qw/graphql";
import { useOpenConfirmation, useToast } from "@qw/ui";
import { useCallback } from "react";

import { useDraftsCompose } from "./use-drafts-compose";

export type UseDeleteDraftReturn = [
  (id: string) => Promise<FetchResult<DeleteInboxDraftMutation> | undefined>,
  MutationResult<DeleteInboxDraftMutation>,
];

export const useDeleteDraft = (): UseDeleteDraftReturn => {
  const { toast } = useToast();
  const { closeDraft } = useDraftsCompose();
  const { openConfirmation } = useOpenConfirmation();
  const [deleteDraftMutation, result] = useDeleteInboxDraftMutation();

  const deleteDraft = useCallback(
    async (id: string) => {
      const confirmed = await openConfirmation({
        title: "Delete draft",
        description: "Are you sure you want to delete this draft? This action cannot be undone.",
        buttonProps: { theme: "destructive", children: "Delete" },
      });

      if (!confirmed) {
        return;
      }

      return deleteDraftMutation({
        variables: { id },
        onCompleted: (data) => {
          if (!data?.deleteInboxDraft) {
            throw new Error("Failed to delete draft");
          }
          toast({
            title: "Draft deleted",
            description: "Your draft has been deleted.",
          });
          closeDraft(id);
        },
        onError: (error) => {
          toast({
            title: "Error deleting draft",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxDraft", "InboxConversations", "InboxDraftsCount"],
      });
    },
    [deleteDraftMutation, openConfirmation, toast, closeDraft]
  );

  return [deleteDraft, result];
};
